interface WhyThisWorksSectionProps {
  onOpenForm: () => void;
}

const problems = [
  {
    icon: 'ri-google-line',
    problem: 'Standard SEO only targets Google',
    solution: 'We optimize for every search platform — AI tools, voice, maps, and Google simultaneously.',
    bad: 'Google only',
    good: 'All 4 platforms',
  },
  {
    icon: 'ri-eye-off-line',
    problem: 'Most websites exist — they don\'t convert',
    solution: 'Your site is built from the ground up to turn visitors into calls, leads, and customers.',
    bad: 'Looks nice, does nothing',
    good: 'Built to convert',
  },
  {
    icon: 'ri-robot-2-line',
    problem: 'AI search is ignored by 95% of businesses',
    solution: 'ChatGPT, Gemini, and Perplexity answer questions directly. We make sure your business gets cited.',
    bad: 'Invisible to AI',
    good: 'Cited by AI tools',
  },
  {
    icon: 'ri-time-line',
    problem: 'SEO agencies charge for activity, not results',
    solution: 'We track leads, rankings, and revenue impact. You know exactly what you\'re getting every month.',
    bad: 'Monthly reports, no ROI',
    good: 'Results you can measure',
  },
];

export default function WhyThisWorksSection({ onOpenForm }: WhyThisWorksSectionProps) {
  return (
    <section className="bg-[#0d1117] py-24 md:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-emerald-400 text-xs font-bold tracking-widest uppercase mb-4">The Mechanism</p>
          <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-5">
            Why Most SEO Doesn&apos;t Work<br />
            <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
              (And Why This Does)
            </span>
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto">
            Search changed in 2024. Most agencies are still running 2019 playbooks — focused on Google alone, ignoring AI, voice, and conversion. Here&apos;s the difference.
          </p>
        </div>

        {/* Problem → Solution grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14">
          {problems.map((item) => (
            <div
              key={item.problem}
              className="bg-white/3 border border-white/8 rounded-2xl p-7 hover:border-emerald-500/25 hover:bg-white/5 transition-all duration-300 group"
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-emerald-500/15 border border-emerald-500/20 flex-shrink-0">
                  <i className={`${item.icon} text-emerald-400`} style={{ fontSize: '20px' }}></i>
                </div>
                <div>
                  <p className="text-white/35 text-xs font-semibold uppercase tracking-widest mb-1">The problem</p>
                  <p className="text-white/70 text-sm leading-snug">{item.problem}</p>
                </div>
              </div>

              {/* Divider with arrow */}
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-white/6"></div>
                <div className="w-6 h-6 flex items-center justify-center bg-emerald-500/20 rounded-full">
                  <i className="ri-arrow-down-line text-emerald-400" style={{ fontSize: '12px' }}></i>
                </div>
                <div className="flex-1 h-px bg-white/6"></div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-emerald-400" style={{ fontSize: '18px' }}></i>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">{item.solution}</p>
              </div>

              {/* Bad vs Good pill */}
              <div className="flex items-center gap-3 mt-5">
                <span className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 rounded-full px-3 py-1">
                  <i className="ri-close-line text-red-400" style={{ fontSize: '11px' }}></i>
                  <span className="text-red-300/80 text-xs">{item.bad}</span>
                </span>
                <i className="ri-arrow-right-line text-white/20" style={{ fontSize: '12px' }}></i>
                <span className="flex items-center gap-1.5 bg-emerald-500/12 border border-emerald-500/25 rounded-full px-3 py-1">
                  <i className="ri-check-line text-emerald-400" style={{ fontSize: '11px' }}></i>
                  <span className="text-emerald-300 text-xs">{item.good}</span>
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-5 bg-gradient-to-r from-emerald-500/10 to-teal-500/8 border border-emerald-500/20 rounded-2xl px-8 py-6">
          <div>
            <p className="text-white font-bold text-lg mb-1">Your competitors are still Google-only. You won&apos;t be.</p>
            <p className="text-white/45 text-sm">Get a free preview — built for AI, voice, maps, and Google. No obligation.</p>
          </div>
          <button
            onClick={onOpenForm}
            className="whitespace-nowrap flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm px-7 py-3.5 rounded-full hover:opacity-90 hover:scale-[1.02] transition-all duration-200 cursor-pointer flex-shrink-0"
          >
            Get Free Website Preview
            <i className="ri-arrow-right-line" style={{ fontSize: '15px' }}></i>
          </button>
        </div>

      </div>
    </section>
  );
}
